/**
 * TrainingFields: the chain's GRPO training settings on a team's config
 * row (api services/pipeline_chain.py `grpo-training`). The switch, the
 * preset a run trains with and its maximum steps. The Evals config page and
 * the Add benchmark dialog both edit them, and both offer them only while
 * task generation is on, since the run trains what the gate proves. The
 * dialog shows the switch alone (TrainingSwitchField) and sends the
 * chain's default preset (usePresetChoices).
 *
 * The preset picker lists the chain's default preset first as Default,
 * then the other presets that fit under "Other presets". A stack whose
 * trainer does not offer the default lists the fitting presets alone and
 * proposes none (`presetChoices`).
 */

import { getApiErrorDetail } from '@/api/client'
import { FieldLabel } from '@/components/shared/InfoTip'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { usePresetChoices } from '@/hooks/usePresetChoices'
import { describedBy, tipId } from '@/lib/describedBy'

import {
  DEFAULT_TRAINING_MAX_STEPS,
  HELD_OUT_NOTE,
  stepsFromInput,
  type PresetChoices,
  type TrainingValues,
} from './training'

const SWITCH_TIP =
  "A GRPO run starts once enough new tasks pass the gate. The team's Pipeline page sets that threshold."
const PRESET_TIP =
  'The training recipe used for each run. Only recipes compatible with this benchmark are listed.'
const STEPS_TIP = "A run stops after this many steps, or earlier at its preset's epoch limit."
const GPU_NOTE = 'Each training run rents GPUs until it ends.'

interface TrainingFieldsProps {
  idPrefix: string
  values: TrainingValues
  /** The outside model routes the scored benchmark's adapter declares. */
  routes: readonly string[]
  disabled?: boolean
  onChange: (changes: Partial<TrainingValues>) => void
}

export function TrainingFields({
  idPrefix,
  values,
  routes,
  disabled = false,
  onChange,
}: TrainingFieldsProps): React.JSX.Element {
  return (
    <div className="space-y-4">
      <TrainingSwitchField
        idPrefix={idPrefix}
        values={values}
        routes={routes}
        disabled={disabled}
        onChange={onChange}
      />
      {values.enabled && (
        <div className="border-l pl-4">
          <TrainingSettingsFields
            idPrefix={idPrefix}
            values={values}
            routes={routes}
            disabled={disabled}
            onChange={onChange}
          />
        </div>
      )}
    </div>
  )
}

/** The switch with its notes, and why no run could start. The Add benchmark dialog shows it alone. */
export function TrainingSwitchField({
  idPrefix,
  values,
  routes,
  disabled = false,
  onChange,
}: TrainingFieldsProps): React.JSX.Element {
  const { choices, error } = usePresetChoices(routes)
  return (
    <div className="space-y-2">
      <TrainingSwitch
        id={`${idPrefix}-training`}
        values={values}
        choices={choices}
        disabled={disabled}
        onChange={onChange}
      />
      {error !== null && (
        <p className="text-xs text-destructive" role="alert">
          {getApiErrorDetail(error) ?? "The trainer's presets could not be read."}
        </p>
      )}
      {choices !== undefined && choices.chainDefault === null && choices.others.length === 0 && (
        <p className="text-xs text-destructive">
          No preset this stack offers gives an address for every model route of this benchmark,
          so no run could start.
        </p>
      )}
    </div>
  )
}

/** The preset and the step limit a run trains with. */
function TrainingSettingsFields({
  idPrefix,
  values,
  routes,
  disabled = false,
  onChange,
}: TrainingFieldsProps): React.JSX.Element {
  const { choices } = usePresetChoices(routes)
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <PresetSelect
        id={`${idPrefix}-training-preset`}
        value={values.preset}
        choices={choices ?? { chainDefault: null, others: [] }}
        disabled={disabled}
        onChange={(preset) => {
          onChange({ preset })
        }}
      />
      <StepsField
        id={`${idPrefix}-training-steps`}
        value={values.maxSteps}
        disabled={disabled}
        onChange={(maxSteps) => {
          onChange({ maxSteps })
        }}
      />
    </div>
  )
}

/** The switch, and the held-out note while it is on. */
function TrainingSwitch({
  id,
  values,
  choices,
  disabled,
  onChange,
}: {
  id: string
  values: TrainingValues
  choices: PresetChoices | undefined
  disabled: boolean
  onChange: (changes: Partial<TrainingValues>) => void
}): React.JSX.Element {
  const noteId = `${id}-held-out`
  const costId = `${id}-cost`
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <Switch
          id={id}
          checked={values.enabled}
          // Turning training on needs the presets; turning it off never does.
          disabled={disabled || (choices === undefined && !values.enabled)}
          aria-describedby={describedBy(tipId(id), costId, values.enabled && noteId)}
          onCheckedChange={(enabled) => {
            onChange({
              enabled,
              preset: values.preset ?? choices?.chainDefault?.name ?? null,
              maxSteps: values.maxSteps ?? DEFAULT_TRAINING_MAX_STEPS,
            })
          }}
        />
        <FieldLabel htmlFor={id} label="Train automatically" tip={SWITCH_TIP} />
      </div>
      <p id={costId} className="text-xs text-muted-foreground">
        {GPU_NOTE}
      </p>
      {values.enabled && (
        <p id={noteId} className="text-xs text-muted-foreground">
          {HELD_OUT_NOTE}
        </p>
      )}
    </div>
  )
}

/** What the closed picker shows for the chosen preset. */
function chosenText(value: string, choices: PresetChoices): string {
  if (choices.chainDefault?.name === value) return 'Default — General tasks'
  return choices.others.find((preset) => preset.name === value)?.label ?? value
}

function PresetSelect({
  id,
  value,
  choices,
  disabled,
  onChange,
}: {
  id: string
  value: string | null
  choices: PresetChoices
  disabled: boolean
  onChange: (preset: string) => void
}): React.JSX.Element {
  const summaryId = `${id}-summary`
  const listed =
    choices.chainDefault === null ? choices.others : [choices.chainDefault, ...choices.others]
  const chosen = listed.find((preset) => preset.name === value)
  return (
    <div className="space-y-2">
      <FieldLabel htmlFor={id} label="Training preset" tip={PRESET_TIP} />
      <Select value={value ?? ''} disabled={disabled} onValueChange={onChange}>
        <SelectTrigger
          id={id}
          aria-describedby={describedBy(tipId(id), chosen !== undefined && summaryId)}
        >
          <SelectValue placeholder="Pick a preset">
            {value === null ? undefined : chosenText(value, choices)}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <PresetOptions choices={choices} />
        </SelectContent>
      </Select>
      {chosen !== undefined && (
        <p id={summaryId} className="text-xs text-muted-foreground">
          {chosen.summary}
        </p>
      )}
    </div>
  )
}

/** Default first, then the other presets under their heading; no heading without a Default. */
export function PresetOptions({ choices }: { choices: PresetChoices }): React.JSX.Element {
  const others = choices.others.map((preset) => (
    <SelectItem key={preset.name} value={preset.name}>
      {preset.label}
    </SelectItem>
  ))
  if (choices.chainDefault === null) return <>{others}</>
  return (
    <>
      <SelectItem value={choices.chainDefault.name}>
        <span className="flex flex-col">
          <span>Default — General tasks</span>
          <span className="text-xs text-muted-foreground">{choices.chainDefault.label}</span>
        </span>
      </SelectItem>
      {others.length > 0 && (
        <>
          <SelectSeparator />
          <SelectGroup>
            <SelectLabel>Other presets</SelectLabel>
            {others}
          </SelectGroup>
        </>
      )}
    </>
  )
}

function StepsField({
  id,
  value,
  disabled,
  onChange,
}: {
  id: string
  value: number | null
  disabled: boolean
  onChange: (maxSteps: number | null) => void
}): React.JSX.Element {
  return (
    <div className="space-y-2">
      <FieldLabel htmlFor={id} label="Maximum training steps" tip={STEPS_TIP} />
      <Input
        id={id}
        type="number"
        min={2}
        max={1000}
        className="font-mono"
        disabled={disabled}
        aria-describedby={tipId(id)}
        value={value === null ? '' : String(value)}
        onChange={(e) => {
          onChange(stepsFromInput(e.target.value))
        }}
      />
    </div>
  )
}
